/**
 * Ciclo de revisão de uma apreciação: o cliente devolve o PDF comentado, os
 * comentários viram checklist e a apreciação fecha em "tramitado após revisão".
 * Espelha o que o backend devolve em /api/revisoes.
 */

export type StatusRevisao = 'aberta' | 'em_andamento' | 'concluida' | 'cancelada';
export type StatusComentarioRevisao = 'pendente' | 'em_andamento' | 'feito' | 'nao_aplicavel';

export interface ComentarioResumo {
  total: number;
  pendentes: number;
  emAndamento: number;
  feitos: number;
  naoAplicaveis: number;
}

export interface RevisaoResumo {
  id: string;
  /** Quantas vezes o cliente devolveu esta apreciação com comentários. */
  rodada: number;
  /**
   * Nº da revisão do DOCUMENTO — o que sai na coluna Nº da capa e no sufixo do
   * Nº Emitente (`_01`). Pode diferir da rodada em documento antigo que já
   * tinha revisão registrada antes de existir ciclo no sistema.
   */
  numeroRevisao: number | null;
  /** Texto da coluna DESCRIÇÃO na linha de revisão da capa. */
  descricaoCapa: string | null;
  status: StatusRevisao;
  origem: 'cliente' | 'interna';
  recebidaEm: string;
  concluidaEm: string | null;
  observacoes: string | null;
  arquivoNome: string | null;
  arquivoUrl: string | null;
  apreciacao: {
    id: string;
    numero: string;
    /** Já com o sufixo `_01` quando o ciclo está aberto. */
    numeroEmitente: string | null;
    titulo: string;
    status: string;
    projetoId: string;
    equipamento: string | null;
    tag: string | null;
  };
  guiaOrigem: { id: string; numero: string } | null;
  guiaRetorno: { id: string; numero: string } | null;
  registradaPor: string | null;
  concluidaPor: string | null;
  comentarios: ComentarioResumo;
}

export interface ItemRevisao {
  id: string;
  indice: number;
  pagina: number;
  /** Subtipo da anotação PDF (Text, Highlight, StrikeOut, FreeText) ou "Manual". */
  tipo: string;
  autor: string | null;
  dataComentario: string | null;
  texto: string;
  trechoDestacado: string | null;
  posX: number | null;
  posY: number | null;
  parte: string | null;
  /** Parte inferida pela leitura do PDF e ainda não confirmada por ninguém. */
  parteAuto: boolean;
  status: StatusComentarioRevisao;
  resolucao: string | null;
  resolvidoEm: string | null;
  responsavel: string | null;
  responsavelId: string | null;
  resolvidoPor: string | null;
}

export interface RevisaoDetalhe extends RevisaoResumo {
  partesDisponiveis: string[];
  itens: ItemRevisao[];
}

/** Item do checklist como ele aparece na aba Partes — versão enxuta. */
export interface ItemChecklist {
  id: string;
  indice: number;
  pagina: number;
  tipo: string;
  autor: string | null;
  texto: string;
  trechoDestacado: string | null;
  parte: string | null;
  parteAuto: boolean;
  status: StatusComentarioRevisao;
  resolucao: string | null;
}

export interface ChecklistRevisao {
  revisaoId: string;
  rodada: number;
  status: StatusRevisao;
  recebidaEm: string;
  arquivoUrl: string | null;
  guiaOrigem: { id: string; numero: string } | null;
  resumo: ComentarioResumo;
  partes: Array<{ parte: string; resumo: ComentarioResumo; itens: ItemChecklist[] }>;
  /** Comentários que a leitura não conseguiu ancorar numa parte. */
  semParte: ItemChecklist[];
}

export interface AnotacaoAnalisada {
  indice: number;
  pagina: number;
  tipo: string;
  autor: string | null;
  data: string | null;
  texto: string;
  trechoDestacado: string | null;
  parte: string | null;
}

export interface AnalisePdfRetorno {
  totalPaginas: number;
  partesDetectadas: string[];
  apreciacao: { id: string; numero: string; titulo: string; status: string; projetoId: string } | null;
  anotacoes: AnotacaoAnalisada[];
  arquivoNome: string;
}

export interface IndicadoresRevisao {
  ciclosAbertos: number;
  ciclosConcluidos: number;
  comentariosPendentes: number;
  comentariosResolvidos: number;
}

export const STATUS_REVISAO_META: Record<StatusRevisao, { rotulo: string; tom: string }> = {
  aberta: { rotulo: 'Aberta', tom: 'bg-amber-50 text-amber-800 dark:bg-amber-900/25 dark:text-amber-200' },
  em_andamento: { rotulo: 'Em andamento', tom: 'bg-blue-50 text-schmersal-blue dark:bg-blue-900/25 dark:text-blue-200' },
  concluida: { rotulo: 'Concluída', tom: 'bg-green-50 text-green-800 dark:bg-green-900/25 dark:text-green-200' },
  cancelada: { rotulo: 'Cancelada', tom: 'bg-slate-100 text-slate-600 dark:bg-slate-700/40 dark:text-slate-300' },
};

export const STATUS_ITEM_META: Record<StatusComentarioRevisao, { rotulo: string; tom: string }> = {
  pendente: { rotulo: 'Pendente', tom: 'bg-amber-50 text-amber-800 dark:bg-amber-900/25 dark:text-amber-200' },
  em_andamento: { rotulo: 'Fazendo', tom: 'bg-blue-50 text-schmersal-blue dark:bg-blue-900/25 dark:text-blue-200' },
  feito: { rotulo: 'Feito', tom: 'bg-green-50 text-green-800 dark:bg-green-900/25 dark:text-green-200' },
  nao_aplicavel: { rotulo: 'Não aplicável', tom: 'bg-slate-100 text-slate-600 dark:bg-slate-700/40 dark:text-slate-300' },
};

/** Como o comentário foi feito no PDF — ajuda a achar a marcação na página. */
export const TIPO_ANOTACAO_ROTULO: Record<string, string> = {
  Text: 'Balão',
  Highlight: 'Destaque',
  StrikeOut: 'Tachado',
  Underline: 'Sublinhado',
  Squiggly: 'Ondulado',
  FreeText: 'Caixa de texto',
  Square: 'Retângulo',
  Circle: 'Elipse',
  Manual: 'Digitado',
};
